import { Eye } from '@hendrx/icons/Eye';
import { Box, Button, Dialog } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import groupBy from 'lodash/groupBy';
import { CSSProperties, lazy, Suspense, useEffect, useState } from 'react';
import { Doc as YDoc, Map as YMap, Text as YText, XmlFragment as YXmlFragment, Array as YArray } from 'yjs';

import classes from './YjsDevTools.module.scss';
import { useCurrentDocument } from '../YjsProvider';

const DynamicReactJson = lazy(() => import('react-json-view'));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getYjsTypeMap(value: any, seen = new WeakSet()): any {
  if (value === null || value === undefined) {
    return { type: String(value) };
  }

  if (seen.has(value)) {
    return { type: 'Circular Reference' };
  }

  if (typeof value === 'object') {
    seen.add(value);
  }

  if (value instanceof YMap) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = {} as any;
    value.forEach((v, k) => {
      result[k] = getYjsTypeMap(v, seen);
    });
    return { type: 'YMap', contents: result };
  }

  if (value instanceof YArray) {
    return {
      type: 'YArray',
      length: value.length,
      contents: Array.from(value).map(v => getYjsTypeMap(v, seen))
    };
  }

  if (value instanceof YXmlFragment) {
    return { type: 'Y.YXmlFragment', length: value.length };
  }

  if (value instanceof YDoc) {
    return { type: 'Y.YDoc', length: 0 };
  }

  if (value instanceof YText) {
    return { type: 'YText', length: value.length };
  }

  if (Array.isArray(value)) {
    return {
      type: 'Array',
      length: value.length,
      contents: value.map(v => getYjsTypeMap(v, seen))
    };
  }

  if (typeof value === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = {} as any;
    for (const [k, v] of Object.entries(value)) {
      result[k] = getYjsTypeMap(v, seen);
    }
    return { type: 'Object', contents: result };
  }

  return { type: typeof value };
}

type DevToolsProps = {
  document: YDoc;
  style?: CSSProperties;
};

function DevTools(props: DevToolsProps) {
  const { document: propsDocument, style } = props;

  const [opened, { close, toggle }] = useDisclosure();
  const document = propsDocument;
  const [doc, setDoc] = useState(() => document?.toJSON() ?? {});

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__YJS_DOCUMENT__ = document;
    if (opened) {
      const handler = () => {
        const documentJSON = document.toJSON();
        setDoc({
          ...documentJSON,
          widgets: groupBy(Object.values(documentJSON.root?.contentObjects ?? {}), 'type'),
          types: getYjsTypeMap(document.getMap('root'))
        });
      };
      handler();
      document.on('update', handler);
      return () => document.off('update', handler);
    }
    return undefined;
  }, [document, opened]);

  return (
    <>
      <Button onClick={toggle} className={classes.openButton} data-testid="yjs-dev-tools-button" style={style}>
        <Eye />
      </Button>
      <Dialog onClose={close} size="90vw" opened={opened} withCloseButton>
        <Box className={classes.jsonContainer} dir="ltr">
          <Suspense fallback={<div>Loading...</div>}>
            <DynamicReactJson src={doc} collapsed sortKeys name={null} />
          </Suspense>
        </Box>
      </Dialog>
    </>
  );
}
export const YjsDevTools =
  process.env.NODE_ENV === 'development'
    ? DevTools
    : function () {
        return null;
      };
